import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Drawer,
  Divider,
  Skeleton,
  Link
} from '@mui/material';
import {
  Search, Refresh, FileDownload, ArrowUpward, ArrowDownward,
  Close, LocationOn, InfoOutlined, ShoppingCartOutlined,
  LanguageOutlined, LocalPharmacyOutlined, InventoryOutlined
} from '@mui/icons-material';
import axios from 'axios';
import { FixedSizeList as List } from 'react-window';
import * as XLSX from 'xlsx';

const API_BASE = 'https://tbl.zerdegroup.kz/api/v1';

interface NomenclatureItem {
  productZid: string;
  productSku: string;
  productName: string;
  producer: string;
  mnn: string;
  category: string;
  categoryChangeDate: string;
  manager: string;
  barcode: string;
  recordDate: string;
  productFolder?: string;
  productType?: string;
  priceGroup?: string;
}

interface BarcodeAnalysis {
  t?: string; // AI-название (скрываем, используем productName)
  d?: string; // Описание
  c?: string; // Страна
  s?: string; // Сайт / источник
  r?: string; // Способ применения / при покупке
}

interface PharmacyStock {
  pharmacyName: string;
  phNumber: string | null;
  cityName: string | null;
  stockCount: number;
}

type SortField = keyof NomenclatureItem;
type SortDirection = 'asc' | 'desc';

const DRAWER_WIDTH = 420;

const Nomenclature: React.FC = () => {
  const [data, setData] = useState<NomenclatureItem[]>([]);
  const [filteredData, setFilteredData] = useState<NomenclatureItem[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Боковая панель анализа
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<NomenclatureItem | null>(null);
  const [analysis, setAnalysis] = useState<BarcodeAnalysis | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [stockData, setStockData] = useState<PharmacyStock[]>([]);
  const [stockLoading, setStockLoading] = useState(false);

  // Загрузка данных
  const fetchNomenclature = useCallback(async (search: string = '') => {
    try {
      setLoading(true);
      const url = search
        ? `${API_BASE}/nomenclature?search=${encodeURIComponent(search)}`
        : `${API_BASE}/nomenclature`;
      const response = await axios.get(url);
      if (response.data.success) {
        setData(response.data.data);
        setFilteredData(response.data.data);
        if (response.data.lastUpdated) {
          setLastUpdated(new Date(response.data.lastUpdated));
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке номенклатуры:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce поиска + первая загрузка
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        fetchNomenclature(searchQuery);
      } else {
        fetchNomenclature();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchNomenclature]);

  // Обновление кэша
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await axios.post(`${API_BASE}/nomenclature/refresh`);
      await fetchNomenclature(searchQuery);
    } catch (error) {
      console.error('Ошибка при обновлении:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Клик по строке — открываем Drawer и параллельно делаем два запроса
  const handleRowClick = useCallback(async (item: NomenclatureItem) => {
    setSelectedItem(item);
    setDrawerOpen(true);
    setAnalysis(null);
    setAnalysisError(null);
    setStockData([]);
    setAnalysisLoading(true);
    setStockLoading(true);

    // Оба запроса параллельно
    const [aiRes, stockRes] = await Promise.allSettled([
      axios.post(`${API_BASE}/nomenclature/barcode-analyze`, { barcode: item.barcode }),
      axios.get(`${API_BASE}/nomenclature/barcode-stock/${encodeURIComponent(item.barcode)}`)
    ]);

    // Обрабатываем AI-ответ
    if (aiRes.status === 'fulfilled') {
      try {
        const rawText: string = aiRes.value.data?.content?.[0]?.text ?? '';
        const jsonMatch = rawText.match(/```json\s*([\s\S]*?)\s*```/);
        const jsonStr = jsonMatch ? jsonMatch[1] : rawText;
        setAnalysis(JSON.parse(jsonStr));
      } catch {
        setAnalysisError('Не удалось разобрать ответ AI.');
      }
    } else {
      setAnalysisError('Не удалось получить данные от AI. Попробуйте снова.');
    }
    setAnalysisLoading(false);

    // Обрабатываем остатки
    if (stockRes.status === 'fulfilled' && stockRes.value.data?.success) {
      setStockData(stockRes.value.data.data);
    }
    setStockLoading(false);
  }, []);

  const handleCloseDrawer = () => setDrawerOpen(false);

  // Экспорт в Excel
  const handleExport = useCallback(() => {
    try {
      setExporting(true);
      const exportData = filteredData.map(item => ({
        'Наименование': item.productName,
        'Производитель': item.producer,
        'МНН': item.mnn,
        'Категория': item.category,
        'Группа (ProductFolder)': item.productFolder || '-',
        'Тип (ProductType)': item.productType || '-',
        'Ценовая группа (PriceGroup)': item.priceGroup || '-',
        'Дата изменения категории': item.categoryChangeDate ? new Date(item.categoryChangeDate).toLocaleDateString('ru-RU') : '-',
        'Ответственный менеджер': item.manager,
        'Штрих-код': item.barcode
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Номенклатура');
      ws['!cols'] = [
        { wch: 50 }, // Наименование
        { wch: 30 }, // Производитель
        { wch: 20 }, // МНН
        { wch: 20 }, // Категория
        { wch: 30 }, // Группа
        { wch: 20 }, // Тип
        { wch: 25 }, // Ценовая группа
        { wch: 22 }, // Дата изменения категории
        { wch: 25 }, // Менеджер
        { wch: 15 }  // Штрихкод
      ];
      const date = new Date().toLocaleDateString('ru-RU').replace(/\./g, '-');
      XLSX.writeFile(wb, `Номенклатура_${date}.xlsx`);
      console.log(`Экспортировано ${exportData.length} записей`);
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
    } finally {
      setExporting(false);
    }
  }, [filteredData]);

  // Форматирование даты обновления
  const formatLastUpdate = useMemo(() => {
    if (!lastUpdated) return 'Загрузка...';
    return lastUpdated.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [lastUpdated]);

  // Обработчик сортировки
  const handleSort = useCallback((field: SortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return field;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  // Отсортированные данные
  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;
    return [...filteredData].sort((a, b) => {
      const valA = (a[sortField] ?? '').toString().toLowerCase();
      const valB = (b[sortField] ?? '').toString().toLowerCase();
      if (sortField === 'categoryChangeDate' || sortField === 'recordDate') {
        const dateA = valA ? new Date(a[sortField]).getTime() : 0;
        const dateB = valB ? new Date(b[sortField]).getTime() : 0;
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
      const cmp = valA.localeCompare(valB, 'ru');
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortField, sortDirection]);

  // Виртуализированная строка таблицы
  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const item = sortedData[index];
    const isSelected = selectedItem?.productZid === item.productZid && drawerOpen;
    return (
      <Box
        style={style}
        onClick={() => handleRowClick(item)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #f4f4f5',
          backgroundColor: isSelected ? '#eef2ff' : (index % 2 === 0 ? 'white' : '#fafafa'),
          transition: 'background-color 0.15s ease',
          cursor: 'pointer',
          borderLeft: isSelected ? '3px solid #6366f1' : '3px solid transparent',
          '&:hover': { backgroundColor: '#eef2ff' }
        }}
      >
        <Box sx={{ flex: '2 0 0', px: 2, py: 1.5, fontSize: '0.875rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#09090b', letterSpacing: '-0.01em' }}>
          <Tooltip title={item.productName} arrow placement="top-start">
            <span>{item.productName}</span>
          </Tooltip>
        </Box>
        <Box sx={{ flex: '1 0 0', px: 2, py: 1.5, fontSize: '0.813rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#52525b' }}>
          <Tooltip title={item.producer || '-'} arrow>
            <span>{item.producer || '-'}</span>
          </Tooltip>
        </Box>
        <Box sx={{ flex: '0.8 0 0', px: 2, py: 1.5, fontSize: '0.813rem', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#52525b' }}>
          <Tooltip title={item.mnn || '-'} arrow>
            <span>{item.mnn || '-'}</span>
          </Tooltip>
        </Box>
        <Box sx={{ flex: '0.8 0 0', px: 2, py: 1.5, fontSize: '0.813rem', textAlign: 'center' }}>
          <Chip
            label={item.category || '-'}
            size="small"
            sx={{
              backgroundColor: item.category ? '#f4f4f5' : 'transparent',
              color: item.category ? '#18181b' : '#a1a1aa',
              fontWeight: 500,
              fontSize: '0.75rem',
              height: '24px',
              borderRadius: '6px',
              border: '1px solid',
              borderColor: item.category ? '#e4e4e7' : 'transparent'
            }}
          />
        </Box>
        <Box sx={{ flex: '1 0 0', px: 2, py: 1.5, fontSize: '0.813rem', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#52525b' }}>
          <Tooltip title={item.productFolder || '-'} arrow>
            <span>{item.productFolder || '-'}</span>
          </Tooltip>
        </Box>
        <Box sx={{ flex: '0.8 0 0', px: 2, py: 1.5, fontSize: '0.813rem', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#52525b' }}>
          <Tooltip title={item.productType || '-'} arrow>
            <span>{item.productType || '-'}</span>
          </Tooltip>
        </Box>
        <Box sx={{ flex: '0.9 0 0', px: 2, py: 1.5, fontSize: '0.813rem', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#52525b' }}>
          <Tooltip title={item.priceGroup || '-'} arrow>
            <span>{item.priceGroup || '-'}</span>
          </Tooltip>
        </Box>
        <Box sx={{ flex: '0.7 0 0', px: 2, py: 1.5, fontSize: '0.75rem', textAlign: 'center', color: '#71717a', fontFamily: '"Geist Mono", "Roboto Mono", monospace' }}>
          {item.categoryChangeDate ? new Date(item.categoryChangeDate).toLocaleDateString('ru-RU') : '-'}
        </Box>
        <Box sx={{ flex: '1 0 0', px: 2, py: 1.5, fontSize: '0.813rem', textAlign: 'center', color: '#52525b', fontWeight: 400 }}>
          {item.manager || '-'}
        </Box>
        <Box sx={{ flex: '0.6 0 0', px: 2, py: 1.5, fontSize: '0.75rem', textAlign: 'center', color: '#71717a', fontFamily: '"Geist Mono", "Roboto Mono", monospace', borderLeft: '1px solid #f4f4f5' }}>
          {item.barcode}
        </Box>
      </Box>
    );
  };

  if (loading && data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '65vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Поля AI-анализа
  const analysisRows = analysis ? [
    {
      icon: <LocationOn sx={{ fontSize: 18, color: '#10b981' }} />,
      label: 'Страна производства',
      value: analysis.c,
      isLink: false
    },
    {
      icon: <InfoOutlined sx={{ fontSize: 18, color: '#3b82f6' }} />,
      label: 'Описание',
      value: analysis.d,
      isLink: false
    },
    {
      icon: <ShoppingCartOutlined sx={{ fontSize: 18, color: '#8b5cf6' }} />,
      label: 'Способ применения',
      value: analysis.r,
      isLink: false
    },
    // {
    //   icon: <LanguageOutlined sx={{ fontSize: 18, color: '#f59e0b' }} />,
    //   label: 'Сайт производителя',
    //   value: analysis.s,
    //   isLink: analysis.s ? /^https?:\/\//i.test(analysis.s) : false
    // },
  ].filter(row => row.value) : [];

  return (
    <Box sx={{ display: 'flex', gap: 0, position: 'relative' }}>
      {/* Основная таблица */}
      <Box sx={{ flex: 1, minWidth: 0, transition: 'all 0.3s ease' }}>
        {/* Поиск и кнопки */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Поиск по наименованию, производителю, МНН..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{
              minWidth: 340, flex: 1,
              '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: 'white' }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#a1a1aa' }} />
                </InputAdornment>
              )
            }}
          />

          <Tooltip title="Обновить данные">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{ bgcolor: 'white', border: '1px solid #e4e4e7', borderRadius: '8px', color: '#18181b', '&:hover': { bgcolor: '#f4f4f5' } }}
              >
                {refreshing ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <FileDownload />}
            onClick={handleExport}
            disabled={exporting || filteredData.length === 0}
            disableElevation
            sx={{
              textTransform: 'none', bgcolor: '#18181b', color: 'white', fontWeight: 500, borderRadius: '8px',
              '&:hover': { bgcolor: '#27272a' }, '&:disabled': { bgcolor: '#e4e4e7', color: '#a1a1aa' }
            }}
          >
            Экспорт в Excel
          </Button>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`Найдено: ${filteredData.length.toLocaleString('ru-RU')}`}
              sx={{ fontWeight: 600, bgcolor: '#f4f4f5', color: '#18181b', borderRadius: '6px' }}
            />
            <Typography variant="caption" sx={{ color: '#71717a', fontFamily: '"Geist Mono", monospace' }}>
              Обновлено: {formatLastUpdate}
            </Typography>
          </Box>
        </Box>

        {/* Таблица */}
        <Paper
          elevation={0}
          sx={{ height: 'calc(100vh - 300px)', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e4e4e7' }}
        >
          {/* Заголовок */}
          <Box sx={{ display: 'flex', background: 'white', borderBottom: '1px solid #e4e4e7', color: '#09090b', position: 'sticky', top: 0, zIndex: 1, fontWeight: 600, fontSize: '0.875rem', letterSpacing: '-0.01em' }}>
            {[
              { field: 'productName' as SortField, label: 'Наименование', flex: '2 0 0' },
              { field: 'producer' as SortField, label: 'Производитель', flex: '1 0 0' },
              { field: 'mnn' as SortField, label: 'МНН', flex: '0.8 0 0', center: true },
              { field: 'category' as SortField, label: 'Категория', flex: '0.8 0 0', center: true },
              { field: 'productFolder' as SortField, label: 'Группа', flex: '1 0 0', center: true },
              { field: 'productType' as SortField, label: 'Тип', flex: '0.8 0 0', center: true },
              { field: 'priceGroup' as SortField, label: 'Цен. группа', flex: '0.9 0 0', center: true },
              { field: 'categoryChangeDate' as SortField, label: 'Дата изм.', flex: '0.7 0 0', center: true },
              { field: 'manager' as SortField, label: 'Менеджер', flex: '1 0 0', center: true },
              { field: 'barcode' as SortField, label: 'Штрих-код', flex: '0.6 0 0', center: true, borderLeft: true },
            ].map(col => (
              <Box
                key={col.field}
                onClick={() => handleSort(col.field)}
                sx={{
                  flex: col.flex, px: 2, py: 1.5,
                  textAlign: col.center ? 'center' : 'left',
                  cursor: 'pointer', userSelect: 'none',
                  display: 'flex', alignItems: 'center',
                  justifyContent: col.center ? 'center' : 'flex-start',
                  gap: 0.5, borderLeft: col.borderLeft ? '1px solid #f4f4f5' : 'none',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { backgroundColor: '#f4f4f5' }
                }}
              >
                {col.label}
                {sortField === col.field && (
                  sortDirection === 'asc'
                    ? <ArrowUpward sx={{ fontSize: 16, color: '#18181b', ml: 0.25 }} />
                    : <ArrowDownward sx={{ fontSize: 16, color: '#18181b', ml: 0.25 }} />
                )}
              </Box>
            ))}
          </Box>

          {sortedData.length > 0 ? (
            <List height={window.innerHeight - 350} itemCount={sortedData.length} itemSize={56} width="100%">
              {Row}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 300 }}>Ничего не найдено</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>Попробуйте изменить параметры поиска</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Боковой Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        variant="persistent"
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            position: 'fixed',
            top: 0,
            height: '100vh',
            borderLeft: '1px solid #e4e4e7',
            boxShadow: '-8px 0 32px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        {/* Шапка */}
        <Box sx={{ px: 3, pt: 3, pb: 2, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ flex: 1, pr: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#09090b', letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                {selectedItem?.productName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: '"Geist Mono", monospace', fontSize: '0.75rem', color: '#71717a', bgcolor: '#f4f4f5', px: 1, py: 0.25, borderRadius: '4px' }}>
                  {selectedItem?.barcode}
                </Typography>
                {selectedItem?.producer && (
                  <Chip label={selectedItem.producer} size="small" sx={{ height: 20, fontSize: '0.72rem', bgcolor: '#eff6ff', color: '#3b82f6', borderRadius: '4px', border: '1px solid #bfdbfe' }} />
                )}
                {selectedItem?.category && (
                  <Chip label={selectedItem.category} size="small" sx={{ height: 20, fontSize: '0.72rem', bgcolor: '#f4f4f5', color: '#52525b', borderRadius: '4px' }} />
                )}
              </Box>
            </Box>
            <IconButton size="small" onClick={handleCloseDrawer} sx={{ color: '#a1a1aa', '&:hover': { color: '#18181b', bgcolor: '#f4f4f5' }, borderRadius: '8px', mt: -0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Прокручиваемый контент */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>

          {/* === AI-анализ === */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a1a1aa', mb: 1.5 }}>
            Анализ штрих-кода
          </Typography>

          {analysisLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={14} thickness={5} />
                <Typography sx={{ fontSize: '0.8rem', color: '#71717a' }}>Запрашиваем у AI...</Typography>
              </Box>
              {[90, 70, 85, 60].map((w, i) => (
                <Skeleton key={i} variant="rounded" width={`${w}%`} height={14} sx={{ borderRadius: '4px' }} />
              ))}
            </Box>
          )}

          {analysisError && !analysisLoading && (
            <Box sx={{ py: 1.5, px: 2, bgcolor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
              <Typography sx={{ color: '#ef4444', fontSize: '0.825rem' }}>{analysisError}</Typography>
            </Box>
          )}

          {!analysisLoading && !analysisError && analysis && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {analysisRows.map((row, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex', gap: 1.5, py: 1.5,
                    borderBottom: i < analysisRows.length - 1 ? '1px solid #f4f4f5' : 'none'
                  }}
                >
                  <Box sx={{ mt: 0.15, flexShrink: 0 }}>{row.icon}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.68rem', color: '#a1a1aa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.4 }}>
                      {row.label}
                    </Typography>
                    {row.isLink ? (
                      <Link href={row.value!} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.85rem', color: '#6366f1', wordBreak: 'break-all' }}>
                        {row.value}
                      </Link>
                    ) : (
                      <Typography sx={{ fontSize: '0.85rem', color: '#18181b', lineHeight: 1.55 }}>
                        {row.value}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}

              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a1a1aa', mt: 1.5, textAlign: 'center' }}>
                Обратите внимание что данные собираются ИИ через открытые источники. Возможны несоответсвия
              </Typography>
            </Box>
          )}

          {/* === Наличие в аптеках === */}
          <Box sx={{ my: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <LocalPharmacyOutlined sx={{ fontSize: 16, color: '#a1a1aa' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a1a1aa' }}>
                Наличие в аптеках
              </Typography>
              {!stockLoading && (
                <Chip
                  label={stockData.length > 0 ? `${stockData.length} аптек` : 'нет'}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: stockData.length > 0 ? '#dcfce7' : '#f4f4f5', color: stockData.length > 0 ? '#16a34a' : '#71717a', borderRadius: '4px', ml: 'auto' }}
                />
              )}
            </Box>

            {stockLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} variant="rounded" height={40} sx={{ borderRadius: '8px' }} />
                ))}
              </Box>
            )}

            {!stockLoading && stockData.length === 0 && (
              <Box sx={{ py: 2, px: 2, bgcolor: '#fafafa', borderRadius: '8px', border: '1px solid #f4f4f5', textAlign: 'center' }}>
                <InventoryOutlined sx={{ color: '#d4d4d8', fontSize: 28, mb: 0.5 }} />
                <Typography sx={{ fontSize: '0.825rem', color: '#a1a1aa' }}>Товар отсутствует в аптеках</Typography>
              </Box>
            )}

            {!stockLoading && stockData.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {stockData.map((ph, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 2, py: 1.25, bgcolor: '#fafafa', borderRadius: '8px',
                      border: '1px solid #f4f4f5',
                      '&:hover': { bgcolor: '#f4f4f5' }, transition: 'background-color 0.15s'
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '0.825rem', fontWeight: 500, color: '#18181b' }}>
                        {ph.pharmacyName}
                      </Typography>
                      {ph.cityName && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#71717a', mt: 0.1 }}>{ph.cityName}</Typography>
                      )}
                    </Box>
                    <Chip
                      label={`${ph.stockCount} шт.`}
                      size="small"
                      sx={{
                        height: 22, fontSize: '0.72rem', fontWeight: 600, borderRadius: '6px',
                        bgcolor: ph.stockCount >= 10 ? '#dcfce7' : '#fef9c3',
                        color: ph.stockCount >= 10 ? '#15803d' : '#854d0e'
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Nomenclature;